import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import './App.css';
import { auth, db } from './firebase/config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import Home from './pages/Home';
import Login from './pages/Login';
import LibraryLayout from './components/LibraryLayout';
import Dashboard from './pages/Dashboard';
import AlbumMappingTool from './pages/AlbumMappingTool';
import StorageManager from './pages/StorageManager';
import AdminPrompt from './components/AdminPrompt';
import Services from './pages/Services';
import YouTubeLibrary from './pages/YouTubeLibrary';
import SpotifyLibrary from './pages/SpotifyLibrary';
import MusicProfile from './pages/MusicProfile';
import Recommendations from './pages/Recommendations';
import RoadTrip from './pages/RoadTrip';
import PuzzleParty from './pages/PuzzleParty';
import NotebookLM from './pages/NotebookLM';
import PatentApplication from './pages/PatentApplication';
import Hitster from './pages/Hitster';
import Members from './pages/Members';
import Logo from './components/Logo';
import NewFeaturesModal from './components/NewFeaturesModal';
import DeviceIndicator from './components/DeviceIndicator';
import { getNavigationItems } from './config/navigation';
import { ToastProvider } from './components/Toast';
import { isMobileDevice } from './config/breakpoints';

// Helper function to detect if we're running on localhost (development)
const isLocalhost = () => {
  const hostname = window.location.hostname;
  // Check for localhost, 127.0.0.1, IPv6 localhost, and private IP ranges
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    // Private IP ranges (192.168.x.x, 10.x.x.x, 172.16-31.x.x)
    /^192\.168\./.test(hostname) ||
    /^10\./.test(hostname) ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)
  );
};

// Helper function to detect if device is mobile (phone) - now based on screen size
const isMobile = () => {
  return isMobileDevice();
};

// Protected Route Component
function ProtectedRoute({ user, loading, children }) {
  const location = useLocation();

  // Check if we're on localhost (development mode)
  const isDev = isLocalhost();

  // Check auth.currentUser directly as a fallback
  // This helps when user prop hasn't updated yet but auth is actually set
  const currentAuthUser = auth.currentUser;

  console.log(
    '🔒 ProtectedRoute check - user prop:',
    user ? user.email : 'null',
    'auth.currentUser:',
    currentAuthUser ? currentAuthUser.email : 'null',
    'loading:',
    loading,
    'location:',
    location.pathname,
    'isLocalhost:',
    isDev
  );

  // Don't redirect while still checking auth
  if (loading) {
    return (
      <div className="App">
        <div className="loading">
          <div className="loading-spinner">🎵</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Use currentAuthUser as fallback if user prop is null
  // This prevents redirect loops when auth state hasn't propagated to user prop yet
  const effectiveUser = user || currentAuthUser;

  // If we're on /login page, don't protect it (let Login component handle it)
  if (location.pathname === '/login') {
    return children;
  }

  // RELAXED MODE FOR LOCALHOST: Allow access without authentication in development
  if (isDev && !effectiveUser && location.pathname !== '/login') {
    console.log(
      '🔓 ProtectedRoute: Localhost detected - allowing access without authentication (development mode)'
    );
    return children;
  }

  // STRICT MODE FOR PRODUCTION: Only redirect if we're not already on the login page
  // AND if we're sure there's no user (check both prop and currentUser)
  if (!isDev && !effectiveUser && location.pathname !== '/login') {
    console.log(
      '🔒 ProtectedRoute: Production mode - No user (checked both prop and currentUser), redirecting to /login'
    );
    return <Navigate to="/login" replace />;
  }

  // If we have a user (from prop or currentUser), allow access
  if (effectiveUser) {
    console.log('🔒 User authenticated, allowing access');
    return children;
  }

  // Fallback: shouldn't reach here, but just in case
  return null;
}

// Simple component to redirect authenticated users away from login page
function AuthRedirectHandler({ user, loading }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user is authenticated and on login page, redirect to library
    // Add a small delay to ensure auth state is fully propagated after redirect
    if (!loading && user && location.pathname === '/login') {
      console.log('✅ User authenticated, redirecting from login to library');
      // Small delay to prevent race condition with redirect result handling
      const timer = setTimeout(() => {
        navigate('/library', { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, loading, location.pathname, navigate]);

  return null;
}

// Mobile redirect component - redirects mobile users from Home to Library
function MobileAwareHome({ user }) {
  const location = useLocation();

  // Check if we should redirect to library (mobile + authenticated)
  if (location.pathname === '/' && isMobile() && user) {
    return <Navigate to="/library" replace />;
  }

  // Still render Home for non-mobile or unauthenticated users
  return <Home user={user} />;
}

// Navigation component to access useNavigate
function Navigation({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Hide navbar on library page (it has its own header)
  const hideNavbar = location.pathname === '/library';

  if (hideNavbar) {
    return null;
  }

  const handleLogoutClick = async () => {
    console.log('🔴 Logout clicked');
    await onLogout();
    console.log('🔴 Signed out, navigating to /login');
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/" className="nav-logo" onClick={handleLinkClick}>
          <Logo size={36} showText={true} />
        </Link>

        {/* Hamburger Menu Button */}
        <button
          className={`nav-hamburger ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <ul className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {location.pathname !== '/' && (
            <li className="nav-item">
              <Link to="/" className="nav-link" onClick={handleLinkClick}>
                Home
              </Link>
            </li>
          )}
          {user ? (
            <>
              {getNavigationItems(true).map((item) => {
                console.log('Navigation item:', item.label, item.path);
                return (
                  <li key={item.path} className="nav-item">
                    {item.external ? (
                      <a
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="nav-link"
                        onClick={handleLinkClick}
                        title={item.title}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.path}
                        className="nav-link"
                        onClick={handleLinkClick}
                        title={item.title}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                );
              })}
              <li className="nav-item">
                <button onClick={handleLogoutClick} className="nav-button">
                  Logout
                </button>
              </li>
            </>
          ) : (
            <li className="nav-item">
              <Link to="/login" className="nav-link" onClick={handleLinkClick}>
                Login
              </Link>
            </li>
          )}
        </ul>
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div className="nav-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}
    </nav>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Global error handler for Firebase internal assertion errors and COOP warnings
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const message = event.reason?.message || String(event.reason) || '';

      // Suppress Firebase internal assertion errors from popup operations
      if (
        message.includes('INTERNAL ASSERTION FAILED') ||
        message.includes('Pending promise was never set')
      ) {
        event.preventDefault(); // Prevent the error from showing in console
        console.log('Suppressed Firebase internal error (popup operation)');
        return;
      }

      // Suppress browser extension message channel errors (harmless)
      if (
        message.includes('message channel closed') ||
        message.includes('asynchronous response') ||
        message.includes('listener indicated an asynchronous response')
      ) {
        event.preventDefault(); // Prevent the error from showing in console
        // This is typically from browser extensions, not our code
        return;
      }
    };

    const handleError = (event) => {
      const message = event.message || event.error?.message || '';

      // Suppress Firebase internal assertion errors
      if (
        message.includes('INTERNAL ASSERTION FAILED') ||
        message.includes('Pending promise was never set')
      ) {
        event.preventDefault(); // Prevent the error from showing in console
        console.log('Suppressed Firebase internal error');
        return;
      }

      // Suppress COOP warnings from Firebase popup (harmless - popup closes naturally)
      if (
        message.includes('Cross-Origin-Opener-Policy') ||
        message.includes('window.close')
      ) {
        event.preventDefault(); // Prevent the warning from showing in console
        return;
      }

      // Suppress 404 errors from Firebase Storage (expected when files don't exist)
      if (
        message.includes('404') &&
        (event.filename?.includes('firebasestorage.googleapis.com') ||
          message.includes('firebasestorage'))
      ) {
        event.preventDefault(); // Prevent 404 errors from showing in console
        return;
      }

      // Suppress browser extension message channel errors (harmless)
      if (
        message.includes('message channel closed') ||
        message.includes('asynchronous response') ||
        message.includes('listener indicated an asynchronous response')
      ) {
        event.preventDefault(); // Prevent the error from showing in console
        // This is typically from browser extensions, not our code
        return;
      }
    };

    // Also catch console warnings (COOP warnings are logged as warnings, not errors)
    const originalWarn = console.warn;
    const originalError = console.error;

    console.warn = (...args) => {
      const message = args.join(' ');
      // Suppress COOP warnings (harmless)
      if (
        message.includes('Cross-Origin-Opener-Policy') &&
        message.includes('window.close')
      ) {
        return; // Don't log this warning
      }
      originalWarn.apply(console, args);
    };

    // Suppress 404 errors from Firebase Storage (expected when files don't exist)
    console.error = (...args) => {
      const message = args.join(' ');

      // Suppress browser extension message channel errors (harmless)
      if (
        message.includes('message channel closed') ||
        message.includes('asynchronous response') ||
        message.includes('listener indicated an asynchronous response') ||
        message.includes(
          'message channel closed before a response was received'
        )
      ) {
        return; // Don't log browser extension errors
      }

      // Suppress 404 errors from Firebase Storage
      if (
        message.includes('404') &&
        (message.includes('firebasestorage.googleapis.com') ||
          message.includes('firebasestorage') ||
          message.includes('storage/object-not-found'))
      ) {
        return; // Don't log expected 404 errors
      }

      originalError.apply(console, args);
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
      window.removeEventListener('error', handleError);
      console.warn = originalWarn; // Restore original console.warn
      console.error = originalError; // Restore original console.error
    };
  }, []);
  const [showNewFeatures, setShowNewFeatures] = useState(false);

  // Check if user has seen the latest announcement
  useEffect(() => {
    const checkAnnouncement = async () => {
      if (!user) {
        setShowNewFeatures(false);
        return;
      }

      try {
        const announcementId = 'new-features-dec-2024'; // Update this when creating new announcements
        const userAnnouncementsRef = doc(db, 'userAnnouncements', user.uid);
        const userAnnouncementsDoc = await getDoc(userAnnouncementsRef);

        // Show if user hasn't seen this announcement
        if (
          !userAnnouncementsDoc.exists() ||
          !userAnnouncementsDoc.data()[announcementId]
        ) {
          // Small delay to let the app load first
          setTimeout(() => {
            setShowNewFeatures(true);
          }, 1000);
        }
      } catch (error) {
        console.error('Error checking announcement status:', error);
        // Don't show on error to avoid annoying users
      }
    };

    if (user && !loading) {
      checkAnnouncement();
    }
  }, [user, loading]);

  useEffect(() => {
    console.log('🔵 App mounted - Setting up auth listener');

    // Set a maximum wait time of 10 seconds for Firebase auth to initialize
    // This gives Firebase time to restore the session from local storage
    const timeout = setTimeout(() => {
      console.log(
        '⏱️ Auth check timeout (10s) - proceeding with current state'
      );
      // If we've been waiting 10 seconds and still loading, something might be wrong
      // But onAuthStateChanged should have fired by now, so this is just a safety net
      setLoading(false);
    }, 10000);

    // onAuthStateChanged will fire immediately with the current auth state
    // and then again whenever the auth state changes
    // This is the key to persistence - it reads from local storage on page load
    let previousUserId = null;
    let authInitialized = false;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      const currentUserId = currentUser?.uid || null;

      console.log(
        '👤 Auth state changed:',
        currentUser ? `✅ Logged in as ${currentUser.email}` : '❌ Logged out'
      );
      if (currentUser) {
        console.log('   UID:', currentUser.uid);
        console.log('   Email:', currentUser.email);
        console.log('   Token valid:', !!currentUser.stsTokenManager);
        console.log(
          '   Last sign in:',
          currentUser.metadata?.lastSignInTime || 'Unknown'
        );
      } else {
        console.log(
          '   No user found - session not persisted or user logged out'
        );
      }

      // Only update state if user actually changed (prevents unnecessary re-renders)
      if (currentUserId !== previousUserId || !authInitialized) {
        clearTimeout(timeout); // Clear the timeout since auth responded
        setUser(currentUser);
        if (!authInitialized) {
          setLoading(false);
          authInitialized = true;
        }
        previousUserId = currentUserId;
      }
    });

    return () => {
      console.log('🔴 App unmounting - Cleaning up auth listener');
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleLogout = async () => {
    try {
      console.log('🔄 Signing out...');
      await signOut(auth);
      console.log('✅ Sign out successful');
    } catch (error) {
      console.error('❌ Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading">
          <div className="loading-spinner">🎵</div>
          <p>Initializing Jookbox Orchestrator...</p>
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App">
          <AuthRedirectHandler user={user} loading={loading} />
          <Navigation user={user} onLogout={handleLogout} />

          <Routes>
            <Route path="/" element={<MobileAwareHome user={user} />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <Dashboard user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <LibraryLayout user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mapping"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <AlbumMappingTool user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/storage"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <StorageManager user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/services"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <Services user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/youtube-library"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <YouTubeLibrary user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/spotify-library"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <SpotifyLibrary user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/music-profile"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <MusicProfile user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recommendations"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <Recommendations user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/members"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <Members user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roadtrip"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <RoadTrip user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/puzzleparty"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <PuzzleParty user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notebooklm"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <NotebookLM user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patent"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <PatentApplication user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hitster"
              element={
                <ProtectedRoute user={user} loading={loading}>
                  <Hitster user={user} />
                </ProtectedRoute>
              }
            />
          </Routes>

          {/* Admin Prompt Panel - only shows for admin users */}
          <AdminPrompt user={user} />

          {/* New Features Modal - shows once per announcement */}
          {showNewFeatures && user && (
            <NewFeaturesModal
              user={user}
              onClose={() => setShowNewFeatures(false)}
            />
          )}

          {/* Device Indicator - only shows in development */}
          <DeviceIndicator />
        </div>
      </Router>
    </ToastProvider>
  );
}

export default App;
